/**
 * Utilities for profile explanation display: human-readable labels, value comparison,
 * and formatting for default vs resolved property display.
 */

/**
 * Converts snake_case, kebab-case, or camelCase to human-readable title phrase.
 * Examples: "is_supported" -> "Is supported", "product-exchange" -> "Product exchange",
 * "isShopNowSupported" -> "Is shop now supported".
 */
export function humanizeKey(key: string): string {
    if (!key || typeof key !== 'string') {
	    return key;
    }
    const withSpaces = key
        .replace(/_/g, ' ')
        .replace(/-/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .trim();
    const words = withSpaces.split(/\s+/).filter(Boolean);

    return words
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
}

/**
 * Deep value equality for default vs resolved comparison.
 * Uses value comparison for arrays and plain objects instead of reference equality.
 */
export function valuesAreEqual(a: unknown, b: unknown): boolean {
    if (a === b) {
	    return true;
    }
    if (a === null || b === null) {
	    return a === b;
    }
    if (typeof a !== typeof b) {
	    return false;
    }
    if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length) {
	        return false;
        }
        return a.every((v, i) => valuesAreEqual(v, b[i]));
    }
    if (typeof a === 'object' && typeof b === 'object') {
        const keysA = Object.keys(a as object);
        const keysB = Object.keys(b as object);
        if (keysA.length !== keysB.length) {
	        return false;
        }
        return keysA.every(
            (k) => keysB.includes(k) && valuesAreEqual((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k])
        );
    }
    return false;
}

/**
 * Format a value for display (boolean, number, string, or object).
 */
export function humanizeValue(value: unknown): string {
    if (value === null) return 'null';
    if (value === undefined) return '–';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') return String(value);
    if (typeof value === 'string') return value;
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
}
